import { create } from 'zustand';
import { getMyJobs, getJobRunDetail } from '@/api/generated/jobs/jobs';
import type { JobRunSummaryDto, JobStepDto } from '@/api/generated/schemas';
import type { CollapseOverride } from './internal/jobsModel';

/**
 * Stav background jobů přihlášeného uživatele.
 *
 * Vědomá odchylka od TanStack Query (viz plán feature): jde o push-updated session stav — zdrojem
 * pravdy jsou SSE delty s monotónní `revision` (out-of-order guard) a REST se volá jen imperativně
 * na hydrataci/refetch (výhradně přes Orval funkce, žádný vlastní fetch). Query cache by se
 * s push deltami prala (staleTime, invalidace, refetch přepisující čerstvější push stav).
 *
 * Sémantika životního cyklu (schválené zadání): hydratace (= F5) načte jen nedokončené a chybné
 * běhy; úspěšně dokončené žijí jen v paměti session (kompaktace/„Odstraň dokončené"), po F5 zmizí.
 */

export interface JobRunEntry {
  summary: JobRunSummaryDto;
  /** Kroky detailu (plochá mapa dle id) — až po prvním otevření detailu. */
  steps?: Record<string, JobStepDto>;
  detailLoaded: boolean;
  detailLoading: boolean;
  /** Nejvyšší viděná revize běhu — SSE eventy se starší revizí se zahazují. */
  revision: number;
  /** Dokončeno mimo pozornost (centrální okno i detail zavřené) → kompaktní řádek přehledu. */
  compact: boolean;
  completedNotified: boolean;
  failedNotified: boolean;
}

interface JobsState {
  runs: Record<string, JobRunEntry>;
  /** Pořadí běhů v přehledu — nejnovější první. */
  order: string[];
  /**
   * Ruční přepnutí sbalení skupin ve stromech detailů (klíč řádku → přepnutí). Výchozí stav si
   * odvozuje strom sám ze stavu skupiny (`defaultCollapsed`) — tady jsou jen odchylky uživatele.
   */
  collapseOverrides: Record<string, CollapseOverride>;
  /** Ticker pro živé doby běhu (aktualizuje JobsRuntime, jen když něco běží). */
  now: number;
  hydrated: boolean;
  /** Zrcadlo stavu UI pro synchronní rozhodování applierů (plní JobsRuntime). */
  centralOpen: boolean;
  openDetailRunIds: string[];

  hydrate: () => Promise<void>;
  refetchAfterReconnect: () => Promise<void>;
  loadDetail: (runId: string) => Promise<void>;

  applyRunChanged: (run: JobRunSummaryDto) => void;
  applyStepChanged: (runId: string, revision: number, step: JobStepDto) => void;
  /** Vrací true, když jde o první notifikaci dokončení (volající zobrazí toast). */
  applyCompleted: (runId: string) => boolean;
  /** Vrací true, když jde o první notifikaci chyby (volající zobrazí toast). */
  applyStepFailed: (runId: string) => boolean;

  /** Ruční přepnutí sbalení skupiny; `auto` je její automatický stav v okamžiku kliknutí. */
  toggleCollapsed: (key: string, collapsed: boolean, auto: boolean) => void;
  /** Odebere běh ze session stavu (po úspěšném dismissu na serveru). */
  removeRun: (runId: string) => void;
  removeCompleted: () => void;
  /** Design `purge()`: dokončené běhy mimo právě sledované → kompaktní řádek. */
  compactCompleted: (keepRunIds: readonly string[]) => void;
  setCentralOpen: (open: boolean) => void;
  setOpenDetailRunIds: (runIds: string[]) => void;
  tick: (now: number) => void;
}

function entryFromSummary(summary: JobRunSummaryDto, previous?: JobRunEntry): JobRunEntry {
  return {
    summary,
    steps: previous?.steps,
    detailLoaded: previous?.detailLoaded ?? false,
    detailLoading: previous?.detailLoading ?? false,
    revision: Math.max(summary.revision, previous?.revision ?? 0),
    compact: previous?.compact ?? false,
    completedNotified: previous?.completedNotified ?? false,
    failedNotified: previous?.failedNotified ?? false,
  };
}

export const useJobsStore = create<JobsState>()((set, get) => ({
  runs: {},
  order: [],
  collapseOverrides: {},
  now: Date.now(),
  hydrated: false,
  centralOpen: false,
  openDetailRunIds: [],

  hydrate: async () => {
    const response = await getMyJobs();
    set((state) => {
      const runs: Record<string, JobRunEntry> = {};
      const order: string[] = [];
      for (const summary of response.jobs) {
        runs[summary.id] = entryFromSummary(summary, state.runs[summary.id]);
        order.push(summary.id);
      }
      return { runs, order, hydrated: true };
    });
  },

  refetchAfterReconnect: async () => {
    // Merge: server vrátí jen nedokončené/chybné — session-dokončené běhy (a jejich kompaktní
    // řádky) musí přežít, dokud je uživatel neodstraní nebo nedá F5.
    const response = await getMyJobs();
    set((state) => {
      const runs = { ...state.runs };
      const order = [...state.order];
      for (const summary of response.jobs) {
        const previous = runs[summary.id];
        if (previous && summary.revision < previous.revision) continue;
        runs[summary.id] = entryFromSummary(summary, previous);
        if (!order.includes(summary.id)) order.unshift(summary.id);
      }
      return { runs, order, hydrated: true };
    });
    // Otevřené detaily si dotáhnou čerstvý strom (SSE eventy mohly během výpadku propadnout).
    for (const runId of get().openDetailRunIds) {
      if (get().runs[runId]?.detailLoaded) void get().loadDetail(runId);
    }
  },

  loadDetail: async (runId) => {
    const existing = get().runs[runId];
    if (existing?.detailLoading) return;
    set((state) => {
      const entry = state.runs[runId];
      if (!entry) return state;
      return { runs: { ...state.runs, [runId]: { ...entry, detailLoading: true } } };
    });
    try {
      const detail = await getJobRunDetail(runId);
      set((state) => {
        const previous = state.runs[runId];
        const steps: Record<string, JobStepDto> = {};
        for (const step of detail.steps) steps[step.id] = step;
        const entry: JobRunEntry = {
          ...entryFromSummary(detail.run, previous),
          steps,
          detailLoaded: true,
          detailLoading: false,
        };
        // Záměrně BEZ vložení do `order` — otevřené okno detailu (např. obnovené po F5) nesmí
        // vrátit dokončený běh do centrálního přehledu (F5 sémantika drží jen nedokončené/chybové).
        return { runs: { ...state.runs, [runId]: entry } };
      });
    } catch {
      set((state) => {
        const entry = state.runs[runId];
        if (!entry) return state;
        return { runs: { ...state.runs, [runId]: { ...entry, detailLoading: false } } };
      });
    }
  },

  applyRunChanged: (run) =>
    set((state) => {
      const previous = state.runs[run.id];
      if (previous && run.revision < previous.revision) return state;
      const entry = entryFromSummary(run, previous);
      // Zotavení po chybě (retry: Failed → Running) povolí případný další chybový toast.
      if (previous?.summary.status === 'Failed' && run.status === 'Running') {
        entry.failedNotified = false;
      }
      const order = state.order.includes(run.id) ? state.order : [run.id, ...state.order];
      return { runs: { ...state.runs, [run.id]: entry }, order };
    }),

  applyStepChanged: (runId, revision, step) =>
    set((state) => {
      const entry = state.runs[runId];
      // Bez načteného detailu se per-step delty neaplikují (souhrn drží run-changed eventy).
      if (!entry?.detailLoaded || !entry.steps) return state;
      if (revision < entry.revision) return state;
      return {
        runs: {
          ...state.runs,
          [runId]: {
            ...entry,
            revision: Math.max(entry.revision, revision),
            steps: { ...entry.steps, [step.id]: step },
          },
        },
      };
    }),

  applyCompleted: (runId) => {
    let firstNotification = false;
    set((state) => {
      const entry = state.runs[runId];
      if (!entry) return state;
      firstNotification = !entry.completedNotified;
      const compact =
        entry.compact || (!state.centralOpen && !state.openDetailRunIds.includes(runId));
      return {
        runs: {
          ...state.runs,
          [runId]: { ...entry, completedNotified: true, compact },
        },
      };
    });
    return firstNotification;
  },

  applyStepFailed: (runId) => {
    let firstNotification = false;
    set((state) => {
      const entry = state.runs[runId];
      if (!entry) return state;
      firstNotification = !entry.failedNotified;
      return { runs: { ...state.runs, [runId]: { ...entry, failedNotified: true } } };
    });
    return firstNotification;
  },

  toggleCollapsed: (key, collapsed, auto) =>
    set((state) => ({
      collapseOverrides: { ...state.collapseOverrides, [key]: { collapsed, auto } },
    })),

  removeRun: (runId) =>
    set((state) => {
      if (!state.runs[runId]) return state;
      const runs = { ...state.runs };
      delete runs[runId];
      return { runs, order: state.order.filter((id) => id !== runId) };
    }),

  removeCompleted: () =>
    set((state) => {
      const keep = state.order.filter((id) => state.runs[id]?.summary.status !== 'Succeeded');
      const runs: Record<string, JobRunEntry> = {};
      for (const id of keep) {
        const entry = state.runs[id];
        if (entry) runs[id] = entry;
      }
      return { runs, order: keep };
    }),

  compactCompleted: (keepRunIds) =>
    set((state) => {
      const runs = { ...state.runs };
      let changed = false;
      for (const [id, entry] of Object.entries(runs)) {
        if (entry.summary.status === 'Succeeded' && !entry.compact && !keepRunIds.includes(id)) {
          runs[id] = { ...entry, compact: true };
          changed = true;
        }
      }
      return changed ? { runs } : state;
    }),

  setCentralOpen: (open) => set({ centralOpen: open }),
  setOpenDetailRunIds: (runIds) => set({ openDetailRunIds: runIds }),
  tick: (now) => set({ now }),
}));

/**
 * Otevření detailu jobu volatelné mimo React (toast akce, publish flow). Skutečné otevírání
 * (navigační kernel + geometrie okna) registruje `JobsRuntime` za běhu.
 */
let jobDetailOpener: ((runId: string) => void) | null = null;

export function registerJobDetailOpener(opener: ((runId: string) => void) | null): void {
  jobDetailOpener = opener;
}

export function openJobDetail(runId: string): void {
  jobDetailOpener?.(runId);
}
